/**
 * Antwort-Editor am Ticket (Spec 7.2): TipTap, Umschalter „Antwort ↔ interne
 * Notiz", Textbausteine und Datei-Anhaenge.
 *
 * Die Komponente kennt keine API — sie meldet `onSubmit` nach oben. Der
 * Upload ist die eine Ausnahme, weil das Ergebnis (`attachment_id`) direkt in
 * den Entwurf gehoert; er kommt als Prop herein.
 */
import {
  emptyTiptapDoc,
  renderTiptapText,
  type Attachment,
  type MessageKind,
  type Snippet,
  type TiptapDoc,
} from '@tickethub/shared';
import type { Editor } from '@tiptap/react';
import { Paperclip, Send, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RichTextEditor } from '@/components/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';

type ReplyEditorProps = {
  snippets: Snippet[];
  acceptedExtensions: string[];
  disabled?: boolean;
  /**
   * Ein uebernommener KI-Entwurf (Spec 9.2). Wechselt die `id`, ersetzt der
   * Editor seinen Inhalt und merkt sich die Herkunft — sie geht als
   * `ai_draft_id` mit, wenn der Agent **selbst** auf Senden klickt.
   */
  aiDraft?: { id: string; body_json: TiptapDoc } | null;
  onUpload: (file: File) => Promise<Attachment>;
  onSubmit: (input: {
    kind: Exclude<MessageKind, 'inbound'>;
    body_json: TiptapDoc;
    attachment_ids: string[];
    ai_draft_id: string | null;
  }) => Promise<void>;
};

export function ReplyEditor({
  snippets,
  acceptedExtensions,
  disabled = false,
  aiDraft = null,
  onUpload,
  onSubmit,
}: ReplyEditorProps) {
  const { t } = useTranslation();

  const [kind, setKind] = useState<Exclude<MessageKind, 'inbound'>>('outbound');
  const [body, setBody] = useState<TiptapDoc>(emptyTiptapDoc());
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [pending, setPending] = useState(false);
  const [uploading, setUploading] = useState(false);
  /** Aus welchem Entwurf der aktuelle Text stammt — `null` = selbst getippt. */
  const [aiDraftId, setAiDraftId] = useState<string | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const isEmpty = renderTiptapText(body).trim().length === 0;

  const handleReady = useCallback((editor: Editor) => {
    editorRef.current = editor;
  }, []);

  /**
   * Entwurf uebernehmen: Text ersetzen, auf „Antwort" umschalten, Herkunft
   * merken. Der Effekt laeuft nur beim **Wechsel** der Entwurfs-ID, sonst
   * wuerde jede Eingabe des Agents wieder ueberschrieben.
   */
  useEffect(() => {
    if (!aiDraft || aiDraft.id === aiDraftId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Entwurf uebernehmen ist genau das
    setAiDraftId(aiDraft.id);
    setBody(aiDraft.body_json);
    setKind('outbound');
    editorRef.current?.commands.setContent(aiDraft.body_json);
  }, [aiDraft, aiDraftId]);

  function insertSnippet(snippetId: string) {
    const snippet = snippets.find((entry) => entry.id === snippetId);
    if (!snippet || !editorRef.current) return;
    editorRef.current.chain().focus().insertContent(snippet.body_json).run();
  }

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // dieselbe Datei soll erneut waehlbar sein
    if (!file) return;

    setUploading(true);
    try {
      const attachment = await onUpload(file);
      setAttachments((current) => [...current, attachment]);
    } finally {
      setUploading(false);
    }
  }

  async function submit() {
    if (isEmpty || pending) return;
    setPending(true);
    try {
      await onSubmit({
        kind,
        body_json: body,
        attachment_ids: attachments.map((attachment) => attachment.id),
        ai_draft_id: aiDraftId,
      });
      setBody(emptyTiptapDoc());
      setAttachments([]);
      setAiDraftId(null);
      editorRef.current?.commands.clearContent();
    } finally {
      setPending(false);
    }
  }

  return (
    <section
      className={cn(
        'rounded-lg border p-4',
        kind === 'internal_note' && 'border-amber-400/60 bg-amber-50/50 dark:bg-amber-950/20',
      )}
      aria-label={t('ticket.reply_title')}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={kind === 'outbound' ? 'default' : 'outline'}
          onClick={() => setKind('outbound')}
        >
          {t('ticket.kind_outbound')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={kind === 'internal_note' ? 'default' : 'outline'}
          onClick={() => setKind('internal_note')}
        >
          {t('ticket.kind_internal_note')}
        </Button>

        {snippets.length > 0 ? (
          <label className="ml-auto flex items-center gap-2 text-sm">
            <span className="sr-only">{t('snippet.insert')}</span>
            <Select
              className="h-8 w-56"
              value=""
              aria-label={t('snippet.insert')}
              onChange={(event) => insertSnippet(event.target.value)}
            >
              <option value="">{t('snippet.insert')}</option>
              {snippets.map((snippet) => (
                <option key={snippet.id} value={snippet.id}>
                  {snippet.title}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
      </div>

      <RichTextEditor
        value={body}
        onChange={setBody}
        onReady={handleReady}
        disabled={disabled || pending}
        ariaLabel={t('ticket.reply_body')}
      />

      {attachments.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs"
            >
              {attachment.filename}
              <button
                type="button"
                aria-label={t('ticket.attachment_remove', { name: attachment.filename })}
                onClick={() =>
                  setAttachments((current) =>
                    current.filter((entry) => entry.id !== attachment.id),
                  )
                }
              >
                <X aria-hidden="true" className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-3 flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept={acceptedExtensions.map((extension) => `.${extension}`).join(',')}
          onChange={(event) => void handleFile(event)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Paperclip aria-hidden="true" />
          {uploading ? t('common.loading') : t('ticket.attach')}
        </Button>

        <Button
          type="button"
          size="sm"
          className="ml-auto"
          disabled={disabled || pending || isEmpty}
          onClick={() => void submit()}
        >
          <Send aria-hidden="true" />
          {kind === 'outbound' ? t('ticket.send_reply') : t('ticket.save_note')}
        </Button>
      </div>
    </section>
  );
}
